import type { JobSummary } from './types';

/**
 * The shift, as one line, from up to three fields that are independently absent.
 *
 * **Roughly one order in four has a type and nothing else** — Evergreen orders are ~28% of the pool and the
 * 2026-08-18 probe found times and days populated on 4 of 175 of them, against 95% for every other type
 * [decision 0083]. So this is the normal case, not the edge: the frame's `Night 6:00p–2:30a` and its bare
 * `1st shift` are two renderings of the same slot.
 *
 * Pure, and separate from the card, because it is the one piece of this feature with branches worth pinning
 * in a test — and because both the list card and the detail screen's *Shift* row read it, so a second copy
 * would be a second answer.
 *
 * Never renders a half range. `6:00 AM –` with a missing end is worse than the type alone: it reads as a
 * shift that never finishes.
 */
export function shiftLine(
  job: Pick<JobSummary, 'shiftType' | 'shiftStart' | 'shiftEnd' | 'shiftDays'>,
): string | null {
  const range =
    job.shiftStart !== null && job.shiftStart !== '' && job.shiftEnd !== null && job.shiftEnd !== ''
      ? `${job.shiftStart}–${job.shiftEnd}`
      : null;

  const parts = [job.shiftType, range, job.shiftDays].filter(
    (part): part is string => part !== null && part !== '',
  );

  return parts.length === 0 ? null : parts.join(' · ');
}
